import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from '../config/index.js';

const AuthContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

function persistSession(user, token) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

function clearSession() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, restore the cached user and re-validate the token against the API.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const cached = localStorage.getItem(USER_STORAGE_KEY);

    if (!token) {
      setLoading(false);
      return;
    }

    if (cached) {
      try {
        setUser(JSON.parse(cached));
      } catch {
        clearSession();
      }
    }

    api
      .me()
      .then((fresh) => {
        setUser(fresh);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(fresh));
      })
      .catch(() => {
        clearSession();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // The session lives in localStorage, which every tab shares. When another
  // tab signs in as someone else (or signs out), this tab's in-memory user is
  // stale — reload onto the dashboard so it picks up the new session instead
  // of rendering the old user's page against the new user's token (→ 403).
  useEffect(() => {
    const onStorage = (e) => {
      // key === null means storage was cleared in another tab.
      if (e.key !== null && (e.key !== TOKEN_STORAGE_KEY || e.oldValue === e.newValue)) return;
      window.location.replace('/dashboard');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = useCallback(async (email, password) => {
    const { user: u, token } = await api.login({ email, password });
    persistSession(u, token);
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (email, name, password) => {
    const { user: u, token } = await api.register({ email, name, password });
    persistSession(u, token);
    setUser(u);
    return u;
  }, []);

  // Swap in a fresher copy of the signed-in user (e.g. after a password change).
  const updateUser = useCallback((u) => {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(u));
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  // RBAC capability check from the user's effective permissions (sent by the
  // API on login / me). Platform admins receive every capability as true.
  const hasCapability = useCallback(
    (capability) => Boolean(user?.permissions?.[capability]),
    [user]
  );

  // Business-role check (e.g. 'Pilot'). The API sends the user's mapped business
  // role names on login / me; access is authorized against these.
  const roles = user?.roles || [];
  const hasRole = useCallback(
    (...names) => names.flat().some((n) => (user?.roles || []).includes(n)),
    [user]
  );

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    permissions: user?.permissions || {},
    roles,
    hasCapability,
    hasRole,
    login,
    register,
    updateUser,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
